// umlaut-check: skip — "VorschlagZeile" ist ein Komponenten-/Dateiname
// (technischer Bezeichner), bleibt ASCII.

import React, {useEffect, useState} from "react";
import axios from "axios";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import {Link} from "@inertiajs/react";
import Farbfeld from "./Farbfeld.jsx";
import AuslaufendAbzeichen from "./AuslaufendAbzeichen.jsx";
import SelectList from "../SelectList.jsx";
import VorschlagBegruendung from "./VorschlagBegruendung.jsx";
import VorschlagVerwerfenModal from "./VorschlagVerwerfenModal.jsx";
import {formatFarbnummer, formatFaktor, formatKw, formatMenge, dringlichkeitLabel, DRINGLICHKEIT_BADGE, statusLabel, STATUS_BADGE} from "./format.js";
import {friendlyErrorMessage} from "../SalesAnalysis/format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

/**
 * Eine Artikelzeile im Bestellvorschlag (Auftrag AP9 Punkt 3): Menge,
 * Dringlichkeit, Ankunfts-KW, Begründung, Oskars Vergleichswert, Status,
 * Zuständigkeit und - nur mit "cosmos.order" - die Aktionen
 * Übernehmen/Verwerfen/Zurücksetzen. Löst nichts aus: die Aktionen ändern
 * ausschließlich den Datensatz in cosmos_vorschlaege.
 */
export default function VorschlagZeile({zeile, benutzer, canOrder, canArtikel, onGeaendert}) {
    const {addToast} = useExternalControls();
    const [busy, setBusy] = useState(false);
    const [zeigeVerwerfen, setZeigeVerwerfen] = useState(false);
    const [benutzerListe, setBenutzerListe] = useState(benutzer);

    useEffect(() => {
        setBenutzerListe(benutzer);
    }, [benutzer]);

    // Ein zugewiesener Benutzer ohne (mehr) Cosmos-Recht fehlt in der
    // gemeinsamen Liste des Seitenaufrufs (VorschlaegeController::benutzer())
    // - für genau diese Zeile per "zugewiesen_user_id" nachladen, damit die
    // bestehende Zuweisung sichtbar bleibt (Vertrag mit K3, 19.09.2026).
    useEffect(() => {
        const zugewiesenId = zeile.zustaendig?.id;
        if (!zugewiesenId || !canOrder || (benutzer || []).some((b) => b.id === zugewiesenId)) {
            return;
        }
        axios
            .get("/cosmos/daten/vorschlaege/benutzer", {params: {zugewiesen_user_id: zugewiesenId}})
            .then(({data}) => setBenutzerListe(data.benutzer || benutzer))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zeile.zustaendig?.id, canOrder]);

    const melden = (fehler) => addToast(<FlexToast variant="danger" title="Bestellvorschläge" autohide={false} message={fehler} />);

    const setzeStatus = (status, grund) => {
        setBusy(true);
        axios
            .post(`/cosmos/daten/vorschlaege/${zeile.id}/status`, {status, grund})
            .then(() => {
                setZeigeVerwerfen(false);
                onGeaendert();
            })
            .catch((e) => melden(friendlyErrorMessage(e, "Der Status konnte nicht geändert werden.")))
            .finally(() => setBusy(false));
    };

    const setzeZustaendig = (userId) => {
        setBusy(true);
        axios
            .post(`/cosmos/daten/vorschlaege/${zeile.id}/zustaendig`, {user_id: userId || null})
            .then(() => onGeaendert())
            .catch((e) => melden(friendlyErrorMessage(e, "Die Zuständigkeit konnte nicht geändert werden.")))
            .finally(() => setBusy(false));
    };

    const artikelName = `${zeile.farbnummer !== null && zeile.farbnummer !== undefined ? formatFarbnummer(zeile.farbnummer) + " " : ""}${zeile.farbname || zeile.artikel_nummer}`;
    const benutzerOptionen = [{value: "", label: "– niemand –"}, ...(benutzerListe || []).map((b) => ({value: String(b.id), label: b.name}))];

    return (
        <tr>
            <td>
                <Farbfeld farbwert={zeile.farbwert} />
            </td>
            <td>
                {canArtikel ? (
                    <Link href={`/cosmos/artikel/${zeile.artikel_nummer}`} className="text-decoration-none">
                        {artikelName}
                    </Link>
                ) : (
                    artikelName
                )}
                {zeile.auslaufend && (
                    <>
                        {" "}
                        <AuslaufendAbzeichen />
                    </>
                )}
                <div className="small text-muted">{zeile.artikel_nummer}</div>
            </td>
            <td>{formatMenge(zeile.menge)}</td>
            {/* #24 (zweite Sichtprüfung): Paletten mit einer Nachkommastelle statt
                gerundet auf ganze Paletten ("26,8" statt fälschlich "27"). */}
            <td>{formatFaktor(zeile.paletten, 1)}</td>
            <td>
                <span className={`badge ${DRINGLICHKEIT_BADGE[zeile.dringlichkeit] || "text-bg-secondary"}`}>{dringlichkeitLabel(zeile.dringlichkeit)}</span>
            </td>
            <td>{formatKw(zeile.ankunft_kw)}</td>
            <td>
                <VorschlagBegruendung begruendung={zeile.begruendung} />
            </td>
            <td className="small text-muted">{zeile.oskar_vorschlag !== null && zeile.oskar_vorschlag !== undefined ? formatMenge(zeile.oskar_vorschlag) : "–"}</td>
            <td>
                <span className={`badge ${STATUS_BADGE[zeile.status] || "text-bg-secondary"}`}>{statusLabel(zeile.status)}</span>
                {zeile.status === "verworfen" && zeile.grund_verworfen && (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-grund-${zeile.id}`}>{zeile.grund_verworfen}</Tooltip>}>
                        <span className="ms-1 small text-muted" style={{cursor: "help"}}>
                            (Grund)
                        </span>
                    </OverlayTrigger>
                )}
            </td>
            <td style={{minWidth: 170}}>
                {canOrder ? (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-zustaendig-${zeile.id}`}>Wer diesen Vorschlag bearbeitet – wirkt sich nur auf die Anzeige/Filterung aus, nicht auf die Bestellung selbst.</Tooltip>}>
                        <span className="d-inline-block w-100">
                            <SelectList
                                value={zeile.zustaendig ? String(zeile.zustaendig.id) : ""}
                                options={benutzerOptionen}
                                onChange={(v) => setzeZustaendig(v || null)}
                                size="sm"
                                ariaLabel={`Zuständig für ${artikelName}`}
                                disabled={busy}
                            />
                        </span>
                    </OverlayTrigger>
                ) : (
                    zeile.zustaendig?.name || "–"
                )}
            </td>
            {canOrder && (
                <td className="text-nowrap">
                    {zeile.status !== "uebernommen" && (
                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-uebernehmen-${zeile.id}`}>Markiert den Vorschlag als übernommen – löst selbst keine Bestellung, keine Mail und keine Weclapp-Übertragung aus.</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-sm btn-outline-success me-1" disabled={busy} onClick={() => setzeStatus("uebernommen")}>
                                    Übernehmen
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                    {zeile.status !== "verworfen" && (
                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-verwerfen-${zeile.id}`}>Markiert den Vorschlag als verworfen, mit Begründung – bestellt oder storniert nichts.</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-sm btn-outline-danger me-1" disabled={busy} onClick={() => setZeigeVerwerfen(true)}>
                                    Verwerfen
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                    {zeile.status !== "offen" && (
                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-zuruecksetzen-${zeile.id}`}>Setzt den Vorschlag zurück auf „offen“.</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setzeStatus("offen")}>
                                    Zurücksetzen
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                </td>
            )}

            <VorschlagVerwerfenModal
                show={zeigeVerwerfen}
                artikelname={artikelName}
                busy={busy}
                onConfirm={(grund) => setzeStatus("verworfen", grund)}
                onCancel={() => setZeigeVerwerfen(false)}
            />
        </tr>
    );
}
